import React from 'react';
import { FormikProps } from 'formik';
import { useIntl } from 'react-intl';

import { errorMsgForField } from 'app/shared/utils/form';
import {
  SurveyQuestion,
  SurveyQuestionOption,
} from 'app/shared/typings/surveyQuestions';
import { PrimaryButton } from 'app/shared/components/atoms/Button';
import GenericFormContainer from 'app/shared/components/atoms/GenericFormContainer';
import { Col, Grid } from 'app/shared/components/atoms/GridManualCSS';
import { Spacer } from 'app/shared/components/atoms/Spacer';
import { Textfield } from 'app/shared/components/atoms/TextfieldManualCSS';
import FormGroup from 'app/shared/components/molecules/FormGroup';
import Select from 'app/shared/components/molecules/SelectManualCSS';

interface SurveyQuestionFieldProps<T = any> {
  formikProps: FormikProps<T>;
  surveyQuestion: SurveyQuestion;
}

const SurveyQuestionField = ({
  formikProps,
  surveyQuestion,
}: SurveyQuestionFieldProps) => {
  const intl = useIntl();

  const { setFieldValue, touched, errors, values } = formikProps;

  const singleOptionSelectOptions =
    surveyQuestion.questionType == 'single_option_select'
      ? [...(surveyQuestion.surveyQuestionOptions || [])].sort(
          (a: SurveyQuestionOption, b: SurveyQuestionOption) =>
            (a.position || 0) > (b.position || 0) ? 1 : -1
        )
      : [];

  const errorMsgKey =
    surveyQuestion.questionType == 'free_text'
      ? `surveyAnswers.${surveyQuestion.id}.answerText`
      : surveyQuestion.questionType == 'single_option_select'
        ? `surveyAnswers.${surveyQuestion.id}.optionId`
        : '';

  return (
    <>
      <Grid cols={2}>
        <Col xs={4} md={4} lg={1} xl={1} key={1}>
          <Spacer mb={1} />
          {surveyQuestion.questionText}
        </Col>
        <Col xs={4} md={4} lg={1} xl={1} key={2}>
          <FormGroup
            data-qaid={`organizer-survey-form-group-${surveyQuestion.id}`}
            label=""
            errorMsg={errorMsgForField(errorMsgKey, touched, errors)}
          >
            <>
              {surveyQuestion.questionType == 'free_text' && (
                <Textfield
                  type="text"
                  id={`surveyQuestion-${surveyQuestion.id}`}
                  name={`surveyQuestion-${surveyQuestion.id}`}
                  value={
                    values.surveyAnswers[surveyQuestion.id?.toString() || '']
                      .answerText || ''
                  }
                  onChange={(e: any) => {
                    setFieldValue('surveyAnswers', {
                      ...values.surveyAnswers,
                      [surveyQuestion.id?.toString() || '']: {
                        answerText: e.target.value,
                      },
                    });
                  }}
                  maxLength={60}
                />
              )}
              {surveyQuestion.questionType == 'single_option_select' && (
                <Select
                  data-qaid="survey-question-selector"
                  getOptionLabel={(opt: SurveyQuestionOption) =>
                    opt.optionText || ''
                  }
                  defaultValue={undefined}
                  options={singleOptionSelectOptions}
                  placeholder={intl.formatMessage({
                    id: 'select',
                  })}
                  onChange={(surveyQuestionOption: SurveyQuestionOption) => {
                    setFieldValue('surveyAnswers', {
                      ...values.surveyAnswers,
                      [surveyQuestion.id?.toString() || '']: {
                        optionId: surveyQuestionOption.id,
                      },
                    });
                  }}
                />
              )}
            </>
          </FormGroup>
        </Col>
      </Grid>
      <Spacer mb={7} />
    </>
  );
};

interface FormProps<T = any> {
  formikProps: FormikProps<T>;
  surveyQuestions?: SurveyQuestion[];
}

const OrganizerSurveyForm = ({ formikProps, surveyQuestions }: FormProps) => {
  const intl = useIntl();

  const { dirty, isSubmitting, submitForm } = formikProps;

  return (
    <GenericFormContainer dataQaId="organizer-survey-form">
      {surveyQuestions?.map((surveyQuestion: SurveyQuestion) => (
        <SurveyQuestionField
          formikProps={formikProps}
          surveyQuestion={surveyQuestion}
          key={surveyQuestion.id}
        />
      ))}
      <Spacer mb={11} />
      <PrimaryButton
        block
        disabled={!dirty}
        data-qaid="organizer-survey-form-submit-btn"
        onClick={submitForm}
        type="submit"
        loading={isSubmitting}
      >
        {intl.formatMessage({
          id: 'admin.organizerSurvey.submit',
        })}
      </PrimaryButton>
    </GenericFormContainer>
  );
};

export default OrganizerSurveyForm;
